import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Cookie, X, ChevronDown, ChevronUp } from 'lucide-react';

const COOKIE_CONSENT_KEY = 'rarifex_cookie_consent';

export const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [preferences, setPreferences] = useState({
    necessary: true, // Always true, can't be disabled
    analytics: false,
    marketing: false
  });

  useEffect(() => {
    // Check if user has already made a choice
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Small delay for better UX - let the page load first
      const timer = setTimeout(() => setIsVisible(true), 1500);
      return () => clearTimeout(timer);
    }
  }, []);

  const handleAcceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(allAccepted));
    setIsVisible(false);
  };

  const handleAcceptSelected = () => {
    const selected = {
      ...preferences,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(selected));
    setIsVisible(false);
  };

  const handleRejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    };
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify(onlyNecessary));
    setIsVisible(false);
  };

  if (!isVisible) return null;

  return (
    <>
      {/* Subtle backdrop for mobile */}
      <div 
        className="fixed inset-0 bg-black/10 backdrop-blur-[1px] z-[9998] md:hidden"
        onClick={() => {}} // Prevent clicks through
      />
      
      {/* Cookie Banner */}
      <div 
        className={`
          fixed z-[9999] transition-all duration-500 ease-out
          ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-full opacity-0'}
          
          /* Mobile: Bottom sheet */
          bottom-0 left-0 right-0 mx-0 mb-0
          
          /* Desktop: Bottom-left card */
          md:bottom-6 md:left-6 md:right-auto md:max-w-md md:mx-0 md:mb-0
        `}
      >
        <div className="
          bg-white 
          shadow-2xl shadow-black/10
          border border-slate-100
          
          /* Mobile: Full width, rounded top */
          rounded-t-2xl
          
          /* Desktop: Rounded card */
          md:rounded-2xl
          
          overflow-hidden
        ">
          {/* Header */}
          <div className="px-5 pt-5 pb-3">
            <div className="flex items-start gap-3">
              {/* Icon */}
              <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#008080] to-[#006666] flex items-center justify-center flex-shrink-0">
                <Cookie className="w-5 h-5 text-white" />
              </div>
              
              {/* Text */}
              <div className="flex-1 min-w-0">
                <h3 className="text-base font-semibold text-slate-900">
                  La tua privacy conta
                </h3>
                <p className="text-sm text-slate-500 mt-1 leading-relaxed">
                  Utilizziamo i cookie per migliorare la tua esperienza. 
                  <Link 
                    to="/cookie" 
                    className="text-[#008080] hover:underline ml-1 font-medium"
                  >
                    Scopri di più
                  </Link>
                </p>
              </div>
            </div>
          </div>

          {/* Expandable Preferences */}
          <div className={`overflow-hidden transition-all duration-300 ${isExpanded ? 'max-h-80' : 'max-h-0'}`}>
            <div className="px-5 pb-3 space-y-3">
              <div className="h-px bg-slate-100" />
              
              {/* Necessary Cookies */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium text-slate-800">Cookie necessari</p>
                  <p className="text-xs text-slate-500">Essenziali per il funzionamento</p>
                </div>
                <div className="w-10 h-6 bg-[#008080] rounded-full relative opacity-60 cursor-not-allowed">
                  <div className="absolute right-1 top-1 w-4 h-4 bg-white rounded-full shadow" />
                </div>
              </div>

              {/* Analytics Cookies */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium text-slate-800">Cookie analitici</p>
                  <p className="text-xs text-slate-500">Ci aiutano a migliorare il sito</p>
                </div>
                <button
                  onClick={() => setPreferences(p => ({ ...p, analytics: !p.analytics }))}
                  className={`w-10 h-6 rounded-full relative transition-colors duration-200 ${
                    preferences.analytics ? 'bg-[#008080]' : 'bg-slate-200'
                  }`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-200 ${
                    preferences.analytics ? 'right-1' : 'left-1'
                  }`} />
                </button>
              </div>

              {/* Marketing Cookies */}
              <div className="flex items-center justify-between py-2">
                <div>
                  <p className="text-sm font-medium text-slate-800">Cookie marketing</p>
                  <p className="text-xs text-slate-500">Per contenuti personalizzati</p>
                </div>
                <button
                  onClick={() => setPreferences(p => ({ ...p, marketing: !p.marketing }))}
                  className={`w-10 h-6 rounded-full relative transition-colors duration-200 ${
                    preferences.marketing ? 'bg-[#008080]' : 'bg-slate-200'
                  }`}
                >
                  <div className={`absolute top-1 w-4 h-4 bg-white rounded-full shadow transition-all duration-200 ${
                    preferences.marketing ? 'right-1' : 'left-1'
                  }`} />
                </button>
              </div>
            </div>
          </div>

          {/* Actions */}
          <div className="px-5 pb-5 pt-2">
            {/* Toggle preferences button */}
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              data-testid="cookie-customize-btn"
              className="flex items-center gap-1 text-xs text-slate-500 hover:text-slate-700 transition-colors mb-3"
            >
              {isExpanded ? (
                <>
                  <ChevronUp className="w-3.5 h-3.5" />
                  Nascondi preferenze
                </>
              ) : (
                <>
                  <ChevronDown className="w-3.5 h-3.5" />
                  Personalizza
                </>
              )}
            </button>

            {/* Buttons */}
            <div className="flex gap-2">
              {isExpanded ? (
                <>
                  <button
                    onClick={handleRejectAll}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-slate-600 bg-slate-100 hover:bg-slate-200 rounded-xl transition-colors"
                  >
                    Rifiuta
                  </button>
                  <button
                    onClick={handleAcceptSelected}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-[#008080] hover:bg-[#006666] rounded-xl transition-colors"
                  >
                    Salva preferenze
                  </button>
                </>
              ) : (
                <>
                  <button
                    onClick={handleRejectAll}
                    className="px-4 py-2.5 text-sm font-medium text-slate-600 hover:text-slate-800 transition-colors"
                  >
                    Solo necessari
                  </button>
                  <button
                    onClick={handleAcceptAll}
                    className="flex-1 px-4 py-2.5 text-sm font-medium text-white bg-gradient-to-r from-[#008080] to-[#006666] hover:from-[#006666] hover:to-[#005555] rounded-xl transition-all shadow-lg shadow-[#008080]/20"
                  >
                    Accetta tutti
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// Utility function to check consent (can be used elsewhere in the app)
export const getCookieConsent = () => {
  const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
  if (!consent) return null;
  try {
    return JSON.parse(consent);
  } catch {
    return null;
  }
};

// Utility function to check if a specific cookie type is allowed
export const isCookieAllowed = (type) => {
  const consent = getCookieConsent();
  if (!consent) return false;
  return consent[type] === true;
};
